/**
 * Defense-in-depth role gate for the contracts feature.
 *
 * Returns `true` only when the authenticated user's
 * `organization_user.role.name === "OWNER"`. Every other state —
 * MEMBER, MANAGER, legacy ADMIN, unauthenticated, or the
 * `undefined` flash while `/auth/me` is in flight — returns
 * `false`. Non-OWNER users are still redirected off `/squad` by
 * the SE-4345 route guard; this hook is the second layer that
 * prevents mutation affordances from rendering on any surface
 * that might embed a contracts mutation outside the `/squad`
 * Stack (for example, the future Player Page Financials tab from
 * SE-4350's surface matrix).
 *
 * Treating the loading state as "not owner" matches the backend
 * service gate (which always rejects until the `organization_user`
 * junction row is fetched) and keeps the UI from flashing Save /
 * Discard / Edit / Delete controls to non-OWNERs who hydrate the
 * app while offline or during a slow `/auth/me` round-trip.
 * @module features/contracts/hooks/useIsContractOwner
 */

import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { RoleName } from "@/generated/graphql";

/**
 * Returns whether the current user may perform contract mutations.
 * @returns `true` only when `currentUser.role.name === RoleName.Owner`.
 * Returns `false` for MEMBER, MANAGER, deprecated ADMIN, missing
 * role, unauthenticated, and the `undefined` loading state.
 * @remarks Matches the backend `assertOwnerRole` gate which checks
 * `role.name !== "OWNER"` — so the frontend never shows an action
 * the server would reject. The comparison uses the generated
 * `RoleName.Owner` enum value rather than the string literal
 * `"OWNER"` to stay in sync with codegen changes.
 */
export function useIsContractOwner(): boolean {
  const currentUser = useCurrentUser();
  return currentUser?.role?.name === RoleName.Owner;
}
